import Alpine from 'alpinejs';
import { isHtmlInputElement, parseDatasetClassnames } from '../utils/helpers.js';

const TAKEN_STATUSES = new Set(['unavailable', 'reserved', 'booked']);

/**
 * Format a date range for display.
 * Rules:
 * - Same month, current year: "Jan 12-15"
 * - Different months, current year: "Jan 12 - Feb 3"
 * - Same month, different year: "Jan 12-15, 2027"
 * - Different months, same year (not current): "Jan 12 - Feb 3, 2027"
 * - Different years: "Dec 28, 2026 - Jan 3, 2027"
 */
function formatDateRange(dates) {
    if (!dates || dates.length === 0) return '';

    // Single date selected (one click, or same day clicked twice)
    if (dates.length === 1 || dates[0].getTime() === dates[1].getTime()) {
        const d = dates[0];
        const month = d.toLocaleString('en-US', { month: 'short' });
        const day = d.getDate();
        const year = d.getFullYear();
        return year === new Date().getFullYear()
            ? `${month} ${day}`
            : `${month} ${day}, ${year}`;
    }

    const [startDate, endDate] = dates;
    const currentYear = new Date().getFullYear();

    const startMonth = startDate.toLocaleString('en-US', { month: 'short' });
    const endMonth = endDate.toLocaleString('en-US', { month: 'short' });
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();
    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();

    const sameMonth = startDate.getMonth() === endDate.getMonth() && startYear === endYear;
    const sameYear = startYear === endYear;
    const isCurrentYear = startYear === currentYear && endYear === currentYear;

    if (sameMonth && isCurrentYear) {
        // "Jan 12-15"
        return `${startMonth} ${startDay}-${endDay}`;
    }

    if (sameMonth && !isCurrentYear) {
        // "Jan 12-15, 2027"
        return `${startMonth} ${startDay}-${endDay}, ${startYear}`;
    }

    if (sameYear && isCurrentYear) {
        // "Jan 12 - Feb 3"
        return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
    }

    if (sameYear && !isCurrentYear) {
        // "Jan 12 - Feb 3, 2027"
        return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${startYear}`;
    }

    // Different years: "Dec 28, 2026 - Jan 3, 2027"
    return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
}

function formatYmd(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function nightsBetween(start, end) {
    const msPerDay = 1000 * 60 * 60 * 24;
    return Math.round((end - start) / msPerDay);
}

function startOfToday() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
}

function addDays(date, days) {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
}

function startOfCalendarGrid(date, firstDayOfWeek = 0) {
    const start = new Date(date);
    const dayOfWeek = start.getDay();
    const offset = (dayOfWeek - firstDayOfWeek + 7) % 7;
    start.setDate(start.getDate() - offset);
    return start;
}

function endOfCalendarGrid(date, firstDayOfWeek = 0) {
    const end = new Date(date);
    const lastDayOfWeek = (firstDayOfWeek + 6) % 7;
    const dayOfWeek = end.getDay();
    const offset = (lastDayOfWeek - dayOfWeek + 7) % 7;
    end.setDate(end.getDate() + offset);
    return end;
}

function isTakenStatus(status) {
    return TAKEN_STATUSES.has(status);
}

function isSameDay(left, right) {
    return Boolean(left && right && formatYmd(left) === formatYmd(right));
}

function isCheckoutBoundaryDay(date, calendarState) {
    if (!date || !calendarState) {
        return false;
    }

    const row = calendarState.days.get(formatYmd(date));
    if (!row || !isTakenStatus(row.status) || row.ctd) {
        return false;
    }

    const previousRow = calendarState.days.get(formatYmd(addDays(date, -1)));
    return Boolean(previousRow && !isTakenStatus(previousRow.status));
}

function isSelectableRangeEndDate(date, startDate, calendarState) {
    if (!date || !startDate || !calendarState || date <= startDate) {
        return false;
    }

    if (!isTraversableRangeDate(date, startDate, calendarState)) {
        return false;
    }

    const endRow = calendarState.days.get(formatYmd(date));
    if (endRow?.ctd) {
        return false;
    }

    return true;
}

function isTraversableRangeDate(date, startDate, calendarState) {
    if (!date || !startDate || !calendarState || date <= startDate) {
        return false;
    }

    for (let night = new Date(startDate); night < date; night = addDays(night, 1)) {
        const row = calendarState.days.get(formatYmd(night));
        if (!row || isTakenStatus(row.status)) {
            return false;
        }
    }

    return true;
}

function callHook(hook, ...args) {
    if (typeof hook === 'function') {
        hook(...args);
        return;
    }

    if (Array.isArray(hook)) {
        hook.forEach((fn) => {
            if (typeof fn === 'function') fn(...args);
        });
    }
}

export function datepicker() {
    return {
        init() {
            const popovers = Alpine.store('popovers');
            const popoverKey = this.$el?.dataset?.popoverKey;
            this.$nextTick(() => {
                popovers?.register(popoverKey || 'dates', {
                    close: () => this.$refs?.dates?._flatpickr?.close(),
                });
            });
        },

        async datepicker(el, options = {}) {
            if (!isHtmlInputElement(el)) return;

            const popovers = Alpine.store('popovers');
            const popoverKey = this.$el?.dataset?.popoverKey || el?.dataset?.popoverKey || 'dates';

            // Already initialized
            if (el._flatpickr) {
                popovers?.open(popoverKey);
                el._flatpickr.open();
                return;
            }

            // Prevent double-init if called multiple times quickly
            if (el.dataset.flatpickrLoading === '1') return;
            el.dataset.flatpickrLoading = '1';

            try {
                const { default: flatpickr } = await import('flatpickr');

                const datasetClassnames = parseDatasetClassnames(el);
                const listingId = el.dataset.listingId || null;
                const calendarUrl = el.dataset.calendarUrl || null;
                const hasListingCalendar = Boolean(listingId && calendarUrl);

                const userOnReady = options?.onReady;
                const userOnOpen = options?.onOpen;
                const userOnClose = options?.onClose;
                const userOnChange = options?.onChange;
                const userOnMonthChange = options?.onMonthChange;
                const userOnYearChange = options?.onYearChange;
                const userOnDayCreate = options?.onDayCreate;

                const searchStore = Alpine.store('search');
                const calendarState = hasListingCalendar
                    ? {
                          days: new Map(),
                          loadedRanges: new Set(),
                          loadingRanges: new Set(),
                      }
                    : null;

                // Scroll handler: reposition calendar while open (sticky parents)
                let scrollRaf = null;
                const onScroll = () => {
                    if (scrollRaf) return;
                    scrollRaf = requestAnimationFrame(() => {
                        scrollRaf = null;
                        el._flatpickr?._positionCalendar();
                    });
                };

                // Match calendar min-width to the parent form-field
                const formField = el.closest('.form-field');

                const buildVisibleRange = (instance) => {
                    const currentMonth = instance.currentMonth;
                    const currentYear = instance.currentYear;
                    const showMonths = Number(instance.config.showMonths || 1);
                    const firstDayOfWeek = Number(instance.config.locale?.firstDayOfWeek || 0);

                    const monthStart = new Date(currentYear, currentMonth, 1);
                    const monthEnd = new Date(currentYear, currentMonth + showMonths, 0);
                    const fromDate = startOfCalendarGrid(monthStart, firstDayOfWeek);
                    const toDate = endOfCalendarGrid(monthEnd, firstDayOfWeek);

                    return {
                        from: formatYmd(fromDate),
                        to: formatYmd(toDate),
                        key: `${formatYmd(fromDate)}:${formatYmd(toDate)}`,
                    };
                };

                const setCalendarLoading = (instance, isLoading) => {
                    const container = instance?.calendarContainer;
                    if (!container) return;

                    const daysContainer = container.querySelector('.dayContainer') || container;
                    let loader = daysContainer.querySelector('.flatpickr-calendar-loader');

                    if (!loader) {
                        loader = document.createElement('div');
                        loader.className = 'flatpickr-calendar-loader';
                        loader.setAttribute('role', 'status');
                        loader.setAttribute('aria-live', 'polite');
                        loader.innerHTML = `
                            <svg class="flatpickr-calendar-loaderBird" viewBox="88 -2 54 34" fill="none" aria-hidden="true">
                                <path d="M113.675 3.6475C108.686 -1.21584 100.564 -1.21584 95.5739 3.6475C93.7493 5.41026 92.5544 7.61373 92.07 10.0218L88.001 12.304L92.0054 14.5547C92.4414 17.0886 93.6686 19.418 95.5739 21.2752L105.537 30.9861H109.622L97.6084 19.2763C95.7354 17.4506 94.702 15.0268 94.702 12.4614C94.702 9.89586 95.7354 7.44059 97.6084 5.61487C101.468 1.85326 107.765 1.85326 111.625 5.61487L134.779 28.1846H131.114L115.306 12.7761C114.014 11.5013 112.287 10.8088 110.446 10.8088C108.605 10.8088 106.877 11.5013 105.569 12.7761C102.889 15.3888 102.889 19.6383 105.569 22.251L109.251 25.8395C110.93 27.4763 112.9 28.7669 115.112 29.6483C117.324 30.5454 119.65 30.9861 122.023 30.9861H141.755L113.675 3.6475ZM123.493 28.2161L111.625 16.6479C111.059 16.0971 110.155 16.0971 109.59 16.6479C109.025 17.1988 109.025 18.0802 109.59 18.6468L119.117 27.9328C116.178 27.3662 113.433 25.9654 111.302 23.8879L107.62 20.2994C106.07 18.7884 106.07 16.3174 107.62 14.7907C108.379 14.051 109.364 13.6575 110.446 13.6575C111.528 13.6575 112.513 14.0667 113.272 14.7907L127.029 28.2161H123.493Z" fill="currentColor"></path>
                            </svg>
                            <span class="sr-only">Loading available dates</span>
                        `;
                        daysContainer.appendChild(loader);
                    }

                    container.classList.toggle('flatpickr-calendar--loading', isLoading);
                    loader.toggleAttribute('hidden', !isLoading);
                };

                const updateCalendarLoading = (instance) => {
                    setCalendarLoading(instance, Boolean(calendarState?.loadingRanges.size));
                };

                const loadListingCalendar = async (instance) => {
                    if (!calendarState || !listingId || !calendarUrl) {
                        return;
                    }

                    const { from, to, key } = buildVisibleRange(instance);

                    if (calendarState.loadedRanges.has(key) || calendarState.loadingRanges.has(key)) {
                        return;
                    }

                    calendarState.loadingRanges.add(key);
                    updateCalendarLoading(instance);

                    try {
                        const params = new URLSearchParams({
                            listingId,
                            from,
                            to,
                        });

                        const response = await fetch(`${calendarUrl}?${params.toString()}`, {
                            method: 'GET',
                            credentials: 'include',
                            headers: {
                                Accept: 'application/json',
                                'X-Requested-With': 'XMLHttpRequest',
                            },
                        });

                        if (!response.ok) {
                            throw new Error(`Listing calendar request failed (${response.status})`);
                        }

                        const payload = await response.json();
                        const rows = Array.isArray(payload?.data) ? payload.data : [];

                        rows.forEach((row) => {
                            if (!row?.date) return;
                            calendarState.days.set(row.date, {
                                status: row.status || 'available',
                                cta: Boolean(row.cta),
                                ctd: Boolean(row.ctd),
                                minNights: Number(row.minNights || 1),
                            });
                        });

                        calendarState.loadedRanges.add(key);
                        instance.redraw();
                    } catch (error) {
                        console.error('Failed to load listing calendar:', error);
                    } finally {
                        calendarState.loadingRanges.delete(key);
                        updateCalendarLoading(instance);
                    }
                };

                const clearSelection = (instance) => {
                    instance.clear();
                    if (instance.altInput) {
                        instance.altInput.value = '';
                    }
                    el.value = '';
                    if (searchStore) {
                        searchStore.dates = '';
                    }
                };

                const emitMinStay = (detail = {}) => {
                    if (!listingId) return;
                    window.dispatchEvent(
                        new CustomEvent('listing:min-stay', {
                            detail: {
                                listingId,
                                ...detail,
                            },
                        })
                    );
                };

                const validateSelection = (selectedDates, instance) => {
                    if (!calendarState || selectedDates.length === 0) {
                        emitMinStay({ state: 'clear', minNights: 1 });
                        return true;
                    }

                    const startDate = selectedDates[0];
                    const startRow = calendarState.days.get(formatYmd(startDate));

                    if (!startRow) {
                        clearSelection(instance);
                        emitMinStay({ state: 'blocked' });
                        return false;
                    }

                    if (startRow?.cta) {
                        clearSelection(instance);
                        emitMinStay({ state: 'blocked' });
                        return false;
                    }

                    if (selectedDates.length < 2) {
                        emitMinStay({
                            state: 'requirement',
                            minNights: Math.max(1, Number(startRow?.minNights || 1)),
                            startDate: formatYmd(startDate),
                        });
                        return true;
                    }

                    const endDate = selectedDates[1];
                    const endRow = calendarState.days.get(formatYmd(endDate));

                    if (!endRow) {
                        clearSelection(instance);
                        emitMinStay({
                            state: 'blocked',
                            minNights: Math.max(1, Number(startRow?.minNights || 1)),
                            startDate: formatYmd(startDate),
                        });
                        return false;
                    }

                    if (endRow?.ctd) {
                        clearSelection(instance);
                        emitMinStay({
                            state: 'blocked',
                            minNights: Math.max(1, Number(startRow?.minNights || 1)),
                            startDate: formatYmd(startDate),
                        });
                        return false;
                    }

                    for (let night = new Date(startDate); night < endDate; night = addDays(night, 1)) {
                        const row = calendarState.days.get(formatYmd(night));
                        if (!row || TAKEN_STATUSES.has(row.status)) {
                            clearSelection(instance);
                            emitMinStay({
                                state: 'blocked',
                                minNights: Math.max(1, Number(startRow?.minNights || 1)),
                                startDate: formatYmd(startDate),
                                endDate: formatYmd(endDate),
                            });
                            return false;
                        }
                    }

                    const minNights = Math.max(1, Number(startRow?.minNights || 1));
                    const selectedNights = nightsBetween(startDate, endDate);
                    if (selectedNights < minNights) {
                        clearSelection(instance);
                        emitMinStay({
                            state: 'violation',
                            minNights,
                            selectedNights,
                            startDate: formatYmd(startDate),
                            endDate: formatYmd(endDate),
                        });
                        return false;
                    }

                    emitMinStay({
                        state: 'valid',
                        minNights,
                        selectedNights,
                        startDate: formatYmd(startDate),
                        endDate: formatYmd(endDate),
                    });
                    return true;
                };

                const updateYearArrows = (instance) => {
                    const container = instance.calendarContainer;
                    if (!container) return;

                    const prevYear = container.querySelector('.flatpickr-prev-year');
                    const nextYear = container.querySelector('.flatpickr-next-year');
                    const minDate = instance.config.minDate;
                    const maxDate = instance.config.maxDate;

                    if (prevYear) {
                        const disabled = minDate &&
                            (instance.currentYear - 1 < minDate.getFullYear() ||
                             (instance.currentYear - 1 === minDate.getFullYear() &&
                              instance.currentMonth < minDate.getMonth()));
                        prevYear.classList.toggle('flatpickr-disabled', !!disabled);
                    }

                    if (nextYear) {
                        const disabled = maxDate &&
                            (instance.currentYear + 1 > maxDate.getFullYear() ||
                             (instance.currentYear + 1 === maxDate.getFullYear() &&
                              instance.currentMonth > maxDate.getMonth()));
                        nextYear.classList.toggle('flatpickr-disabled', !!disabled);
                    }
                };

                const listingMinDate = hasListingCalendar
                    ? addDays(startOfToday(), 1)
                    : 'today';

                flatpickr(el, {
                    allowInput: false,
                    dateFormat: 'Y-m-d',
                    altInput: true,
                    altFormat: 'M j', // Fallback format, we override in onChange
                    mode: 'range',
                    position: 'top left',
                    minDate: listingMinDate,
                    disable: hasListingCalendar
                        ? [
                              (date) => {
                                  const row = calendarState?.days.get(formatYmd(date));
                                  if (!row) {
                                      return true;
                                  }

                                  const selectedDates = el._flatpickr?.selectedDates ?? [];
                                  const startDate = selectedDates[0] ?? null;
                                  const endDate = selectedDates[1] ?? null;
                                  const isCheckoutBoundary = isCheckoutBoundaryDay(date, calendarState);

                                  if (!startDate) {
                                      return Boolean(row.cta || (isTakenStatus(row.status) && !isCheckoutBoundary));
                                  }

                                  if (selectedDates.length >= 2) {
                                      if (isSameDay(date, startDate)) {
                                          return false;
                                      }

                                      if (
                                          isSameDay(date, endDate) &&
                                          isSelectableRangeEndDate(endDate, startDate, calendarState)
                                      ) {
                                          return false;
                                      }

                                      return Boolean(row.cta || (isTakenStatus(row.status) && !isCheckoutBoundary));
                                  }

                                  if (isSameDay(date, startDate)) {
                                      return false;
                                  }

                                  if (date < startDate) {
                                      return true;
                                  }

                                  return !isTraversableRangeDate(date, startDate, calendarState);
                              },
                          ]
                        : [],
                    prevArrow: '<svg width="100%" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.156.33C5.147.32 4.866.234 4.532.14 4.197.048 3.92-.014 3.916.003 3.912.02 3.877.3 3.84.628 3.763 1.283 3.58 2.048 3.383 2.526 2.77 4.017 1.672 5.004.34 5.261L0 5.326V5.985v.659l.307.062c2.015.41 3.329 2.236 3.575 4.97.016.177.035.323.043.323.067 0 1.194-.329 1.215-.355.015-.019.004-.186-.026-.372C4.789 9.218 3.856 7.546 2.645 6.847l-.29-.168 5.972.013 3.777.009V5.325l-3.787-.009-5.958-.013.366-.225c.951-.584 1.704-1.73 2.148-3.268.126-.438.317-1.437.282-1.48Z" fill="currentColor"/></svg>',
                    nextArrow: '<svg width="100%" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.948 11.67c.01.011.29.096.625.19.334.093.611.155.615.137.005-.017.04-.298.077-.625.076-.655.26-1.42.456-1.899.613-1.49 1.711-2.478 3.044-2.735l.34-.065V6.015v-.659l-.308-.062C9.781 4.884 8.467 3.058 8.222.324 8.206.146 8.186 0 8.179 0c-.068 0-1.194.329-1.215.355-.016.019-.005.186.025.372.326 2.055 1.26 3.727 2.47 4.427l.29.167-5.972-.013L0 5.342v1.333l3.786.009 5.959.013-.367.225c-.951.584-1.704 1.73-2.148 3.268-.126.438-.317 1.437-.282 1.48Z" fill="currentColor"/></svg>',
                    ...options,
                    onChange: (selectedDates, dateStr, instance) => {
                        if (calendarState) {
                            instance.redraw();
                        }

                        if (!validateSelection(selectedDates, instance)) {
                            callHook(userOnChange, selectedDates, dateStr, instance);
                            return;
                        }

                        // Update the display input with custom formatted range
                        if (instance.altInput) {
                            instance.altInput.value = formatDateRange(selectedDates);
                        }
                        // Update the search store with the raw date string
                        if (searchStore) {
                            searchStore.dates = dateStr;
                        }
                        // Also update the hidden input for form submission
                        el.value = dateStr;
                        callHook(userOnChange, selectedDates, dateStr, instance);
                    },
                    onOpen: (selectedDates, dateStr, instance) => {
                        popovers?.open(popoverKey);

                        // Sync min-width with form-field on every open
                        if (formField && instance.calendarContainer) {
                            instance.calendarContainer.style.minWidth =
                                formField.offsetWidth + 'px';
                        }

                        window.addEventListener('scroll', onScroll, { passive: true, capture: true });
                        loadListingCalendar(instance);

                        callHook(userOnOpen, selectedDates, dateStr, instance);
                    },
                    onMonthChange: (selectedDates, dateStr, instance) => {
                        updateYearArrows(instance);
                        loadListingCalendar(instance);
                        callHook(userOnMonthChange, selectedDates, dateStr, instance);
                    },
                    onYearChange: (selectedDates, dateStr, instance) => {
                        updateYearArrows(instance);
                        loadListingCalendar(instance);
                        callHook(userOnYearChange, selectedDates, dateStr, instance);
                    },
                    onDayCreate: (dObj, dStr, instance, dayElem) => {
                        if (calendarState && dayElem?.dateObj) {
                            const row = calendarState.days.get(formatYmd(dayElem.dateObj));
                            const startDate = instance.selectedDates?.[0] ?? null;
                            const isCheckoutBoundary = isCheckoutBoundaryDay(dayElem.dateObj, calendarState);
                            const isSelectableCheckoutBoundary = isSelectableRangeEndDate(
                                dayElem.dateObj,
                                startDate,
                                calendarState,
                            );

                            if (row && isTakenStatus(row.status) && !isSelectableCheckoutBoundary && !isCheckoutBoundary) {
                                dayElem.classList.add('flatpickr-day--taken');
                            }
                            if (row?.cta) dayElem.classList.add('flatpickr-day--cta');
                            if (row?.ctd) dayElem.classList.add('flatpickr-day--ctd');
                        }
                        callHook(userOnDayCreate, dObj, dStr, instance, dayElem);
                    },
                    onReady: (selectedDates, dateStr, instance) => {
                        if (datasetClassnames.length && instance?.calendarContainer) {
                            instance.calendarContainer.classList.add(...datasetClassnames);
                        }

                        // Format initial dates if present (e.g., from URL params)
                        if (instance.altInput && selectedDates.length === 2) {
                            instance.altInput.value = formatDateRange(selectedDates);
                        }

                        // Inject year navigation arrows
                        const monthsContainer = instance.calendarContainer.querySelector('.flatpickr-months');
                        if (monthsContainer) {
                            const prevMonth = monthsContainer.querySelector('.flatpickr-prev-month');
                            const nextMonth = monthsContainer.querySelector('.flatpickr-next-month');

                            const prevYear = document.createElement('span');
                            prevYear.className = 'flatpickr-prev-year';
                            prevYear.setAttribute('role', 'button');
                            prevYear.setAttribute('aria-label', 'Previous year');
                            prevYear.innerHTML = instance.config.prevArrow;
                            prevYear.addEventListener('click', () => {
                                instance.changeYear(instance.currentYear - 1);
                            });

                            const nextYear = document.createElement('span');
                            nextYear.className = 'flatpickr-next-year';
                            nextYear.setAttribute('role', 'button');
                            nextYear.setAttribute('aria-label', 'Next year');
                            nextYear.innerHTML = instance.config.nextArrow;
                            nextYear.addEventListener('click', () => {
                                instance.changeYear(instance.currentYear + 1);
                            });

                            if (prevMonth) monthsContainer.insertBefore(prevYear, prevMonth);
                            if (nextMonth) monthsContainer.appendChild(nextYear);
                            updateYearArrows(instance);
                        }

                        loadListingCalendar(instance);
                        callHook(userOnReady, selectedDates, dateStr, instance);
                    },
                    onClose: (selectedDates, dateStr, instance) => {
                        popovers?.clear(popoverKey);
                        window.removeEventListener('scroll', onScroll, { capture: true });
                        if (scrollRaf) {
                            cancelAnimationFrame(scrollRaf);
                            scrollRaf = null;
                        }

                        callHook(userOnClose, selectedDates, dateStr, instance);
                    },
                });
                popovers?.open(popoverKey);
                el._flatpickr?.open();
            } finally {
                delete el.dataset.flatpickrLoading;
            }
        },

        syncDatepicker(el, dates) {
            if (!isHtmlInputElement(el)) return;
            if (!el._flatpickr) return;

            const instance = el._flatpickr;

            // When the bound value is cleared, also clear Flatpickr internal state.
            if (!dates && (instance.selectedDates?.length || el.value)) {
                instance.clear();
                instance.close();
                if (instance.altInput) {
                    instance.altInput.value = '';
                }
            }
        },
    };
}

document.addEventListener('alpine:init', () => {
    Alpine.data('datepicker', datepicker);
});
